import { FC, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Popover,
  Stack,
  Menu,
  GraphNode,
  TabGroup,
  TabGroupWC,
  TabPanel,
  Tab,
  Grid,
  GridCell,
  TextBody,
  Toggle,
  Spacer,
  Divider,
  TextCaption,
} from '@pypestream/design-system';
import { map, isNil } from 'lodash';
import { observer } from 'mobx-react-lite';

import { IconError } from './icon-error';
// import { IconWarning } from './icon-warning';
import { IconCheck } from './icon-check';
import {
  useProStudioRoutes,
  useCurrentRoute,
  ProStudioPages,
} from '../../hooks';
import {
  NodeColumns,
  tableStore,
  ValidationMode,
  WorkflowError,
} from '../../stores/table-store';
import { downloadPifCompilerDiagnostics } from './download-pif-compiler-diagnostics';
import { usePifPanel } from './use-pif-panel';

import './validation-popover.scss';

interface ValidationPopoverProps {
  isLoading: boolean;
  errors: WorkflowError[];
  showAgentCompilerErrors: boolean;
}

export const ValidationPopover: FC<ValidationPopoverProps> = observer(
  ({ errors, isLoading, showAgentCompilerErrors }) => {
    const [popoverActive, setPopoverActive] = useState(false);
    const [popoverTab, setPopoverTab] = useState<string>();
    const tabsRef = useRef<TabGroupWC | null>(null);
    const { workflows } = useProStudioRoutes();
    const currentRoute = useCurrentRoute();
    const navigate = useNavigate();
    const { diagnostics, dataForCompiling, pifErrors, rows } = usePifPanel();
    const isWorkflowsPage = currentRoute.label === ProStudioPages.WORKFLOWS;
    const errorsAmount = errors.length || 0;

    const handleTabChange = (tab: string) => {
      if (popoverActive && popoverTab === tab) {
        handlePopoverClose();
        return;
      }

      setPopoverTab(tab);

      if (tabsRef.current) {
        tabsRef.current.show(tab);
      }

      setPopoverActive(true);
    };

    const handlePopoverClose = () => {
      setPopoverActive(false);
      setPopoverTab('');
    };

    useEffect(() => {
      if (isLoading) {
        handlePopoverClose();
      }
    }, [isLoading]);

    const successfullyCompiled = showAgentCompilerErrors
      ? errorsAmount === 0 && pifErrors.length === 0
      : errorsAmount === 0;

    const handleErrorClick = useCallback(
      ({
        columnId,
        rowIndex,
        selectCell = true,
      }: {
        columnId: NodeColumns;
        rowIndex: number;
        selectCell?: boolean;
      }) => {
        if (!isWorkflowsPage) {
          const targetPage = Math.floor(rowIndex / tableStore.paginationSize);
          tableStore.currentPage = targetPage;
          tableStore.setPendingScrollToCell({ rowIndex, columnId });
          navigate(workflows.path);
          handlePopoverClose();
          return;
        }
        if (!selectCell) return;
        tableStore.scrollToAndFocusCell(rowIndex, columnId);
        handlePopoverClose();
      },
      [isWorkflowsPage, navigate, workflows.path]
    );

    const handleValidationModeChange = useCallback(() => {
      tableStore.setValidationMode(
        tableStore.validationMode === ValidationMode.Auto
          ? ValidationMode.Manual
          : ValidationMode.Auto
      );
    }, []);

    return (
      <>
        <Stack gutter="xsmall" nowrap id="error-warning-popover-trigger">
          {isLoading ? (
            <span className="c-nav-validation-popover-spinner" />
          ) : (
            <Button
              test-id="ps-validation-popover-trigger"
              size="small"
              className="c-nav-validation-popover-trigger"
              variant={
                successfullyCompiled ? 'ghost' : 'validation-destructive'
              }
              selected={popoverTab === 'errors'}
              onClick={() => handleTabChange('errors')}
            >
              {successfullyCompiled ? (
                <div style={{ margin: -2 }}>
                  <IconCheck testId="ps-validation-popover-icon-check" />
                </div>
              ) : (
                <div slot="prefix">
                  <IconError />
                </div>
              )}
              {!successfullyCompiled && errorsAmount}
            </Button>
          )}
        </Stack>
        <Popover
          size="medium"
          width={showAgentCompilerErrors ? 'auto' : 'default'}
          test-id="ps-validation-popover"
          id="error-popover"
          active={popoverActive}
          trigger="error-warning-popover-trigger"
          disableTriggerListeners
          onAutoClose={handlePopoverClose}
        >
          <div
            style={{ maxWidth: showAgentCompilerErrors ? '760px' : '430px' }}
          >
            <Stack gutter="xsmall" alignItems="center" justifyContent="end">
              <TextBody size="small">Automatic validation</TextBody>
              <Toggle
                size="small"
                checked={tableStore.validationMode === ValidationMode.Auto}
                onChange={handleValidationModeChange}
                disabled={isLoading}
              />
            </Stack>
            <Spacer size="small" />
            <div style={{ marginInline: -16 }}>
              <Divider />
            </div>
            <Spacer />
            {successfullyCompiled ? (
              <Stack alignItems="center" justifyContent="center" gutter="small">
                <IconCheck />
                <TextCaption>
                  There are no errors, your solution is ready to be published.
                </TextCaption>
              </Stack>
            ) : (
              <Grid>
                <GridCell
                  xsmall="12"
                  large={showAgentCompilerErrors ? '6' : '12'}
                >
                  <TabGroup
                    variant="flush"
                    ref={(el) => (tabsRef.current = el as TabGroupWC)}
                  >
                    <Tab
                      slot="tab"
                      panel="errors"
                      onClick={() => {
                        setPopoverTab('errors');
                      }}
                    >
                      Errors {errorsAmount}
                    </Tab>
                    {/* <Tab
              slot="tab"
              panel="warnings"
              onClick={() => setPopoverTab('warnings')}
            >
              Warnings (3)
            </Tab> */}

                    <TabPanel
                      name="errors"
                      style={{
                        marginInline: showAgentCompilerErrors ? 0 : -16,
                      }}
                    >
                      <Menu>
                        {map(
                          errors,
                          (
                            {
                              colId,
                              colIndex,
                              rowIndex,
                              errorMessage,
                              cellNumber,
                              nodeNumber,
                              rowNumber,
                            },
                            idx
                          ) => {
                            const falsyValues = [
                              null,
                              undefined,
                              NaN,
                              Infinity,
                              -Infinity,
                            ];
                            const isClickable =
                              !falsyValues.includes(rowIndex) &&
                              !falsyValues.includes(colIndex);

                            const getLabel = () => {
                              const subLabel =
                                isNil(nodeNumber) && isNil(rowNumber)
                                  ? 'Unknown node error'
                                  : isNil(nodeNumber)
                                    ? `Row: ${isNil(rowNumber) || isNaN(rowNumber) ? 'No info about the row number' : rowNumber}`
                                    : `Node: ${nodeNumber}`;

                              if (colId) {
                                return `Column: "${colId}" - ${subLabel}`;
                              }
                              return subLabel;
                            };

                            return (
                              <GraphNode
                                test-id={`ps-validation-popover-graph-node-${idx}`}
                                key={`${cellNumber ?? 'n'}-${nodeNumber ?? rowNumber ?? 'r'}-${idx}`}
                                role={isClickable ? 'menuitem' : undefined}
                                disabled={!isClickable}
                                variant="flush"
                                status="error"
                                aria-label={
                                  errorMessage ?? 'Unknown validation error'
                                }
                                label={getLabel()}
                                caption={errorMessage}
                                nodeId={
                                  cellNumber ? String(cellNumber) : undefined
                                }
                                onClick={
                                  isClickable
                                    ? () =>
                                        handleErrorClick({
                                          columnId: colId as NodeColumns,
                                          rowIndex: rowIndex ?? 0,
                                        })
                                    : undefined
                                }
                              />
                            );
                          }
                        )}
                      </Menu>
                    </TabPanel>
                    {/* <TabPanel name="warnings" style={{ marginInline: -16 }}>
              <Menu>
                <GraphNode
                  role="menuitem"
                  variant="flush"
                  status="warning"
                  aria-label="Errors Node Name 1"
                  label="Errors Node Name 1"
                  caption="Button is routed to a node that does not exist. Button is routed to a node that does not exist."
                  nodeId="3920394"
                />
                <GraphNode
                  role="menuitem"
                  variant="flush"
                  status="warning"
                  aria-label="Errors Node Name 1"
                  label="Errors Node Name 1"
                  caption="Button is routed to a node that does not exist. Button is routed to a node that does not exist."
                  nodeId="3920394"
                />
                <GraphNode
                  role="menuitem"
                  variant="flush"
                  status="warning"
                  aria-label="Errors Node Name 1"
                  label="Errors Node Name 1"
                  caption="Button is routed to a node that does not exist. Button is routed to a node that does not exist."
                  nodeId="3920394"
                />
              </Menu>
            </TabPanel> */}
                  </TabGroup>
                </GridCell>
                {/*/ / TODO: NEED IT UNTIL WE SWITCH UI TO USE PIF COMPILER*/}
                {showAgentCompilerErrors && (
                  <GridCell xsmall="12" large="6">
                    <TabGroup variant="flush">
                      <Tab
                        slot="tab"
                        panel="errors"
                        onClick={() => {
                          downloadPifCompilerDiagnostics(
                            diagnostics,
                            dataForCompiling,
                            rows
                          );
                        }}
                      >
                        Download Agent Compiler Data
                      </Tab>

                      <TabPanel
                        name="errors"
                        style={{
                          marginInline: showAgentCompilerErrors ? 0 : -16,
                        }}
                      >
                        <Menu>
                          {map(pifErrors, (error: WorkflowError, idx) => {
                            const {
                              colId,
                              colIndex,
                              rowIndex: rI,
                              errorMessage,
                              cellNumber,
                              nodeNumber,
                            } = error;

                            const rowIndex =
                              rI ??
                              tableStore.getSortedRowIndex(
                                tableStore
                                  .getAllSortedRows()
                                  .find((row) => row.nodeNumber === nodeNumber)
                                  ?.id || ''
                              );

                            const falsyValues = [
                              null,
                              undefined,
                              NaN,
                              Infinity,
                              -Infinity,
                            ];
                            const isClickable =
                              !falsyValues.includes(rowIndex) &&
                              !falsyValues.includes(colIndex);

                            return (
                              <GraphNode
                                test-id={`ps-validation-popover-graph-node-${idx}`}
                                key={`${cellNumber ?? 'n'}-${nodeNumber ?? 'r'}-${idx}`}
                                role={isClickable ? 'menuitem' : undefined}
                                disabled={!isClickable}
                                variant="flush"
                                status="error"
                                aria-label={
                                  errorMessage ?? 'Unknown validation error'
                                }
                                label={
                                  colId
                                    ? `Column: "${colId}" - Node: ${nodeNumber || 'unknown'}`
                                    : nodeNumber
                                      ? `Node: ${nodeNumber}`
                                      : 'Unknown node error'
                                }
                                caption={errorMessage}
                                nodeId={
                                  cellNumber ? String(cellNumber) : undefined
                                }
                                onClick={
                                  isClickable
                                    ? () =>
                                        handleErrorClick({
                                          columnId: colId as NodeColumns,
                                          rowIndex: rowIndex ?? 0,
                                        })
                                    : undefined
                                }
                              />
                            );
                          })}
                        </Menu>
                      </TabPanel>
                    </TabGroup>
                  </GridCell>
                )}
              </Grid>
            )}
          </div>
        </Popover>
      </>
    );
  }
);
